import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader';
import ProcessSection from '@/components/sections/ProcessSection';
import Reveal from '@/components/common/Reveal';
import { buttonVariants, Button } from '@/components/ui/button';

const principles = [
  { k: 'Written before drawn',   v: 'Every screen starts as a sentence. If it doesn\'t read well flat, it won\'t read well finished.' },
  { k: 'One layout at a time',   v: 'We share individual screens, not full-site previews. Feedback stays specific, revisions stay small.' },
  { k: 'Handwritten React',      v: 'No page builders, no template markup. The site you ship is the site we can maintain for years.' },
  { k: 'The launch is boring',   v: 'Redirects, analytics, monitoring — all done a week before. Launch day is for coffee.' },
];

export default function ProcessPage() {
  return (
    <motion.main
      id="main-content"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>Process | TechsFlow</title>
      </Helmet>
      <PageHeader
        eyebrow="Index · 04 · Process"
        title="How a TechsFlow site"
        italicTail="is actually made."
        lede="Not a methodology. Not a framework. Six chapters, six deliverables, one sentence for each. Everything else is between us and the work."
      />

      <ProcessSection />

      <section className="py-20 md:py-28 border-t border-border bg-secondary/30">
        <div className="mx-auto container-editorial">
          <Reveal>
            <div className="label-eyebrow mb-4">The four house rules</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] mb-12 tracking-tight max-w-3xl text-balance">
              Principles that <span className="italic-serif text-sienna">outlive any single project.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {principles.map((p, i) => (
              <Reveal key={p.k} delay={i * 0.08}>
                <article className="card-editorial p-8 h-full flex flex-col min-h-[200px]">
                  <span className="font-mono text-xs text-muted-foreground rule-numeric mb-6">R.{i + 1}</span>
                  <h3 className="font-serif text-2xl md:text-3xl mb-3 tracking-tight">{p.k}</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed mt-auto">{p.v}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14">
            <Link to="/contact" className={buttonVariants({ variant: 'editorial' })}>
              Start Chapter I <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}
